/* =====================================================================
   UCHI — Product pages v1 behaviour
   - Gallery: swipe counter + progress bars (mobile)
   - Set switcher: swaps photos, info, box, button and sticky bar, updates URL
   - Refill plans: button label, selling plan, note, per-cup, sticky bar
   - Sticky bar: shows once the main button scrolls away, hides at the footer
   Adding to cart itself is handled by uchi-v3.js ([data-u3-add]).
   ===================================================================== */

const $ = (root, sel) => root.querySelector(sel);
const $$ = (root, sel) => [...root.querySelectorAll(sel)];

function setText(el, value) {
  if (el && value != null) el.textContent = value;
}

/* ---------- Gallery ---------------------------------------------------- */
function initGallery(gal) {
  if (gal.dataset.ready) return;
  gal.dataset.ready = '1';
  const track = $(gal, '[data-gal-track]');
  const counter = $(gal, '[data-gal-i]');
  const bars = $$(gal, '.u3-gal__bars span');
  if (!track) return;
  let raf = 0;
  const update = () => {
    raf = 0;
    const w = track.clientWidth || 1;
    const i = Math.min(bars.length - 1, Math.max(0, Math.round(track.scrollLeft / w)));
    setText(counter, String(i + 1));
    bars.forEach((b, n) => b.classList.toggle('is-on', n === i));
  };
  track.addEventListener('scroll', () => { if (!raf) raf = requestAnimationFrame(update); }, { passive: true });
}

/* ---------- Sticky bar ------------------------------------------------- */
function initSticky(section) {
  const bar = $(section, '[data-pdp-sticky]');
  const cta = $(section, '[data-pdp-cta]');
  if (!bar || !cta || !('IntersectionObserver' in window)) return;

  let pastCta = false;
  let overFooter = false;
  const render = () => {
    const show = pastCta && !overFooter;
    bar.classList.toggle('is-visible', show);
    bar.toggleAttribute('inert', !show);
    bar.setAttribute('aria-hidden', show ? 'false' : 'true');
  };

  new IntersectionObserver(([e]) => {
    pastCta = !e.isIntersecting && e.boundingClientRect.top < 0;
    render();
  }).observe(cta);

  const footer = document.querySelector('footer, .shopify-section-group-footer-group, [data-u3-buybar-hide]');
  if (footer) {
    new IntersectionObserver(([e]) => { overFooter = e.isIntersecting; render(); }).observe(footer);
  }
}

function syncSticky(section, { img, title, price, sub, variant, plan, label }) {
  const bar = $(section, '[data-pdp-sticky]');
  if (!bar) return;
  if (img) {
    const box = $(bar, '[data-pdp-sticky-img]');
    const el = box && $(box, 'img');
    if (el) { el.src = img; el.removeAttribute('srcset'); }
  }
  setText($(bar, '[data-pdp-sticky-title]'), title);
  setText($(bar, '[data-pdp-sticky-price]'), price);
  setText($(bar, '[data-pdp-sticky-sub]'), sub);
  const btn = $(bar, '[data-pdp-sticky-btn]');
  if (btn) {
    if (variant) btn.dataset.variant = variant;
    if (plan !== undefined) {
      if (plan) btn.dataset.plan = plan; else delete btn.dataset.plan;
    }
    if (label) setText($(btn, '[data-u3-label]'), label);
  }
}

/* ---------- Set switcher ----------------------------------------------- */
function initSets(section) {
  const opts = $$(section, '[data-pdp-opt]');
  if (!opts.length) return;
  const cta = $(section, '[data-pdp-cta]');

  const pick = (opt, { push = true, focus = false } = {}) => {
    const i = opt.dataset.pdpOpt;
    opts.forEach((o) => {
      const on = o === opt;
      o.setAttribute('aria-checked', on ? 'true' : 'false');
      o.tabIndex = on ? 0 : -1;
    });
    $$(section, '[data-opt]').forEach((el) => {
      const on = el.dataset.opt === i;
      el.hidden = !on;
      const h = $(el, '.u3-pdp__title');
      if (h) h.setAttribute('aria-hidden', on ? 'false' : 'true');
    });
    if (cta) {
      cta.dataset.variant = opt.dataset.variant;
      setText($(cta, '[data-u3-label]'), opt.dataset.cta);
      const soldOut = opt.dataset.available === 'false';
      cta.disabled = soldOut;
    }
    syncSticky(section, {
      img: opt.dataset.thumb,
      title: opt.dataset.title,
      price: opt.dataset.price,
      sub: opt.dataset.sub,
      variant: opt.dataset.variant,
    });
    // Re-init the now-visible gallery (scroll position / counter)
    const gal = $(section, `.u3-gal[data-opt="${i}"]`);
    if (gal) { initGallery(gal); const t = $(gal, '[data-gal-track]'); if (t) t.scrollLeft = 0; }
    if (push && opt.dataset.url && !window.Shopify?.designMode) {
      const url = new URL(opt.dataset.url, window.location.origin);
      const view = new URLSearchParams(window.location.search).get('view');
      if (view) url.searchParams.set('view', view);
      history.replaceState(history.state, '', url.pathname + url.search);
    }
    if (focus) opt.focus();
  };

  opts.forEach((opt, n) => {
    opt.addEventListener('click', () => pick(opt));
    opt.addEventListener('keydown', (e) => {
      const dir = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 }[e.key];
      if (!dir) return;
      e.preventDefault();
      pick(opts[(n + dir + opts.length) % opts.length], { focus: true });
    });
  });

  const current = opts.find((o) => o.getAttribute('aria-checked') === 'true') || opts[0];
  pick(current, { push: false });
}

/* ---------- Refill plans ----------------------------------------------- */
function initPlans(section) {
  const wrap = $(section, '[data-pdp-plans]');
  if (!wrap) return;
  const cta = $(section, '[data-pdp-cta]');
  const note = $(section, '[data-pdp-note]');
  const cup = $(section, '[data-pdp-cup]');
  const price = $(section, '[data-pdp-price]');

  const apply = (input) => {
    const d = input.dataset;
    if (cta) {
      if (d.plan) cta.dataset.plan = d.plan; else delete cta.dataset.plan;
      setText($(cta, '[data-u3-label]'), d.cta);
    }
    setText(note, d.note || note?.textContent);
    setText(cup, d.cup);
    setText(price, d.price);
    $$(section, '[data-pdp-rec-price]').forEach((el) => setText(el, d.price));
    setText($(section, '[data-pdp-rec-label]'), d.rec);
    syncSticky(section, { price: d.price, sub: d.sticky || d.title, plan: d.plan || '', label: d.short });
  };

  wrap.addEventListener('change', (e) => {
    if (e.target.matches('input[type="radio"]')) apply(e.target);
  });
  const checked = $(wrap, 'input:checked') || $(wrap, 'input[type="radio"]');
  if (checked) { checked.checked = true; apply(checked); }
}

/* ---------- Boot ------------------------------------------------------- */
function init(scope = document) {
  $$(scope, '[data-pdp]').forEach((section) => {
    if (section.dataset.pdpReady) return;
    section.dataset.pdpReady = '1';
    $$(section, '[data-gal]').forEach((g) => { if (!g.hidden) initGallery(g); });
    initSets(section);
    initPlans(section);
    initSticky(section);
  });
}

init();
document.addEventListener('shopify:section:load', (e) => init(e.target));
